import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

interface Props {
  idleLabel: string;
  loadingLabel?: string;
  successLabel?: string;
  errorLabel?: string;
  disabled?: boolean;
  className?: string;
  messageClassName?: string;
  resetAfterMs?: number;
  onAction: () => Promise<void> | void;
}

export default function AsyncActionButton({
  idleLabel,
  loadingLabel = 'Guardando…',
  successLabel = 'Cambios guardados',
  errorLabel = 'No se pudo guardar',
  disabled = false,
  className = '',
  messageClassName = '',
  resetAfterMs = 2400,
  onAction,
}: Props) {
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  async function handleClick() {
    if (disabled || status === 'loading') return;
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setStatus('loading');
    try {
      await onAction();
      setStatus('success');
      timeoutRef.current = window.setTimeout(() => {
        setStatus('idle');
        timeoutRef.current = null;
      }, resetAfterMs);
    } catch {
      setStatus('error');
    }
  }

  const busy = status === 'loading';
  const buttonLabel = busy ? loadingLabel : idleLabel;
  const statusMessage = status === 'success' ? successLabel : status === 'error' ? errorLabel : null;
  const statusTone = status === 'success' ? 'text-ui-success' : 'text-ui-danger';

  return (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || busy}
        className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm disabled:opacity-50 hover:opacity-90 ${className}`}
      >
        {busy && <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden="true" />}
        <span>{buttonLabel}</span>
      </button>

      {statusMessage && (
        <p className={`flex items-center gap-2 text-sm sm:justify-end ${statusTone} ${messageClassName}`}>
          {status === 'success' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
          <span>{statusMessage}</span>
        </p>
      )}
    </div>
  );
}
